import axios from 'axios'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { DailySpendDataResponse } from 'src/models/dailySpendData.models'
import { MonthlySpendDataResponse } from 'src/models/monthlySpendData.models'
import { RootState } from 'src/redux/createRootReducer'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { BackendErrorPayload } from 'src/utils/error.utils'

export const GET_TOTAL_AVAILABLE_CREDIT_INIT =
  'app/home/GET_TOTAL_AVAILABLE_CREDIT_INIT'
export const GET_TOTAL_AVAILABLE_CREDIT_SUCCESS =
  'app/home/GET_TOTAL_AVAILABLE_CREDIT_SUCCESS'
export const GET_TOTAL_AVAILABLE_CREDIT_FAILURE =
  'app/home/GET_TOTAL_AVAILABLE_CREDIT_FAILURE'

export type GetTotalAvailableCreditSuccessPayload = {
  totalAvailableCreditCents: number
}

export type GetTotalAvailableCreditErrorPayload = BackendErrorPayload<string>

export interface GetTotalAvailableCreditInitAction {
  type: typeof GET_TOTAL_AVAILABLE_CREDIT_INIT
}

export interface GetTotalAvailableCreditSuccessAction {
  type: typeof GET_TOTAL_AVAILABLE_CREDIT_SUCCESS
  payload: GetTotalAvailableCreditSuccessPayload
}

export interface GetTotalAvailableCreditFailureAction {
  type: typeof GET_TOTAL_AVAILABLE_CREDIT_FAILURE
  payload: GetTotalAvailableCreditErrorPayload
}

export type GetTotalAvailableCreditActions =
  | GetTotalAvailableCreditInitAction
  | GetTotalAvailableCreditSuccessAction
  | GetTotalAvailableCreditFailureAction

const createGetTotalAvailableCreditInitAction =
  (): GetTotalAvailableCreditInitAction => ({
    type: GET_TOTAL_AVAILABLE_CREDIT_INIT,
  })

const createGetTotalAvailableCreditSuccessAction = (
  payload: GetTotalAvailableCreditSuccessPayload,
): GetTotalAvailableCreditSuccessAction => ({
  type: GET_TOTAL_AVAILABLE_CREDIT_SUCCESS,
  payload,
})

const createGetTotalAvailableCreditFailureAction = (
  payload: GetTotalAvailableCreditErrorPayload,
): GetTotalAvailableCreditFailureAction => ({
  type: GET_TOTAL_AVAILABLE_CREDIT_FAILURE,
  payload,
})

export const getTotalAvailableCredit = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetTotalAvailableCreditActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetTotalAvailableCreditActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetTotalAvailableCreditInitAction())
    return axios
      .get<GetTotalAvailableCreditSuccessPayload>(
        `/totalavailablecredit`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetTotalAvailableCreditSuccessAction(response.data))
        },
        async (err) => {
          dispatch(
            createGetTotalAvailableCreditFailureAction(err.response.data),
          )
        },
      )
  }
}

export const GET_TOTAL_AVAILABLE_CREDIT_V2_INIT =
  'app/home/GET_TOTAL_AVAILABLE_CREDIT_V2_INIT'
export const GET_TOTAL_AVAILABLE_CREDIT_V2_SUCCESS =
  'app/home/GET_TOTAL_AVAILABLE_CREDIT_V2_SUCCESS'
export const GET_TOTAL_AVAILABLE_CREDIT_V2_FAILURE =
  'app/home/GET_TOTAL_AVAILABLE_CREDIT_V2_FAILURE'

export type GetTotalAvailableCreditV2SuccessPayload = {
  totalAvailableCreditCentsUSD: number | null
  totalAvailableCreditCentsCAD: number | null
}

export type GetTotalAvailableCreditV2ErrorPayload = BackendErrorPayload<string>

export interface GetTotalAvailableCreditV2InitAction {
  type: typeof GET_TOTAL_AVAILABLE_CREDIT_V2_INIT
}

export interface GetTotalAvailableCreditV2SuccessAction {
  type: typeof GET_TOTAL_AVAILABLE_CREDIT_V2_SUCCESS
  payload: GetTotalAvailableCreditV2SuccessPayload
}

export interface GetTotalAvailableCreditV2FailureAction {
  type: typeof GET_TOTAL_AVAILABLE_CREDIT_V2_FAILURE
  payload: GetTotalAvailableCreditV2ErrorPayload
}

export type GetTotalAvailableCreditV2Actions =
  | GetTotalAvailableCreditV2InitAction
  | GetTotalAvailableCreditV2SuccessAction
  | GetTotalAvailableCreditV2FailureAction

const createGetTotalAvailableCreditV2InitAction =
  (): GetTotalAvailableCreditV2InitAction => ({
    type: GET_TOTAL_AVAILABLE_CREDIT_V2_INIT,
  })

const createGetTotalAvailableCreditV2SuccessAction = (
  payload: GetTotalAvailableCreditV2SuccessPayload,
): GetTotalAvailableCreditV2SuccessAction => ({
  type: GET_TOTAL_AVAILABLE_CREDIT_V2_SUCCESS,
  payload,
})

const createGetTotalAvailableCreditV2FailureAction = (
  payload: GetTotalAvailableCreditV2ErrorPayload,
): GetTotalAvailableCreditV2FailureAction => ({
  type: GET_TOTAL_AVAILABLE_CREDIT_V2_FAILURE,
  payload,
})

export const getTotalAvailableCreditV2 = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetTotalAvailableCreditV2Actions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetTotalAvailableCreditV2Actions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetTotalAvailableCreditV2InitAction())
    return axios
      .get<GetTotalAvailableCreditV2SuccessPayload>(
        `/totalavailablecredit/v2`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetTotalAvailableCreditV2SuccessAction(response.data))
        },
        async (err) => {
          dispatch(
            createGetTotalAvailableCreditV2FailureAction(err.response.data),
          )
        },
      )
  }
}

export const GET_MONTHLY_SPEND_INIT = 'app/home/GET_MONTHLY_SPEND_INIT'
export const GET_MONTHLY_SPEND_SUCCESS = 'app/home/GET_MONTHLY_SPEND_SUCCESS'
export const GET_MONTHLY_SPEND_FAILURE = 'app/home/GET_MONTHLY_SPEND_FAILURE'

export type GetMonthlySpendSuccessPayload = {
  chartData: {
    dailySpendData: DailySpendDataResponse[]
    organizationId: string
  }
}

export type GetMonthlySpendErrorPayload = BackendErrorPayload<string>

export interface GetMonthlySpendInitAction {
  type: typeof GET_MONTHLY_SPEND_INIT
}

export interface GetMonthlySpendSuccessAction {
  type: typeof GET_MONTHLY_SPEND_SUCCESS
  payload: GetMonthlySpendSuccessPayload
}

export interface GetMonthlySpendFailureAction {
  type: typeof GET_MONTHLY_SPEND_FAILURE
  payload: GetMonthlySpendErrorPayload
}

export type GetMonthlySpendActions =
  | GetMonthlySpendInitAction
  | GetMonthlySpendSuccessAction
  | GetMonthlySpendFailureAction

const createGetMonthlySpendInitAction = (): GetMonthlySpendInitAction => ({
  type: GET_MONTHLY_SPEND_INIT,
})

const createGetMonthlySpendSuccessAction = (
  payload: GetMonthlySpendSuccessPayload,
): GetMonthlySpendSuccessAction => ({
  type: GET_MONTHLY_SPEND_SUCCESS,
  payload,
})

const createGetMonthlySpendFailureAction = (
  payload: GetMonthlySpendErrorPayload,
): GetMonthlySpendFailureAction => ({
  type: GET_MONTHLY_SPEND_FAILURE,
  payload,
})

export const getMonthlySpend = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetMonthlySpendActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetMonthlySpendActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetMonthlySpendInitAction())
    return axios
      .get<GetMonthlySpendSuccessPayload>(
        `/charts/monthlyspend`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetMonthlySpendSuccessAction(response.data))
        },
        (err) => {
          dispatch(createGetMonthlySpendFailureAction(err.response.data))
        },
      )
  }
}

export const GET_SIX_MONTH_SPEND_INIT = 'app/home/GET_SIX_MONTH_SPEND_INIT'
export const GET_SIX_MONTH_SPEND_SUCCESS =
  'app/home/GET_SIX_MONTH_SPEND_SUCCESS'
export const GET_SIX_MONTH_SPEND_FAILURE =
  'app/home/GET_SIX_MONTH_SPEND_FAILURE'

export type GetSixMonthSpendSuccessPayload = {
  organizationId: string
  lastUpdatedAt: string
  monthSpendData: MonthlySpendDataResponse[]
}

export type GetSixMonthSpendErrorPayload = BackendErrorPayload<string>

export interface GetSixMonthSpendInitAction {
  type: typeof GET_SIX_MONTH_SPEND_INIT
}

export interface GetSixMonthSpendSuccessAction {
  type: typeof GET_SIX_MONTH_SPEND_SUCCESS
  payload: GetSixMonthSpendSuccessPayload
}

export interface GetSixMonthSpendFailureAction {
  type: typeof GET_SIX_MONTH_SPEND_FAILURE
  payload: GetSixMonthSpendErrorPayload
}

export type GetSixMonthSpendActions =
  | GetSixMonthSpendInitAction
  | GetSixMonthSpendSuccessAction
  | GetSixMonthSpendFailureAction

const createGetSixMonthSpendInitAction = (): GetSixMonthSpendInitAction => ({
  type: GET_SIX_MONTH_SPEND_INIT,
})

const createGetSixMonthSpendSuccessAction = (
  payload: GetSixMonthSpendSuccessPayload,
): GetSixMonthSpendSuccessAction => ({
  type: GET_SIX_MONTH_SPEND_SUCCESS,
  payload,
})

const createGetSixMonthSpendFailureAction = (
  payload: GetSixMonthSpendErrorPayload,
): GetSixMonthSpendFailureAction => ({
  type: GET_SIX_MONTH_SPEND_FAILURE,
  payload,
})

export const getSixMonthSpend = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetSixMonthSpendActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetSixMonthSpendActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetSixMonthSpendInitAction())
    return axios
      .get<GetSixMonthSpendSuccessPayload>(
        `/charts/sixmonthspend`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetSixMonthSpendSuccessAction(response.data))
        },
        (err) => {
          dispatch(createGetSixMonthSpendFailureAction(err.response.data))
        },
      )
  }
}

export const GET_SIX_MONTH_SPEND_BY_CURRENCY_INIT =
  'app/home/GET_SIX_MONTH_SPEND_BY_CURRENCY_INIT'
export const GET_SIX_MONTH_SPEND_BY_CURRENCY_SUCCESS =
  'app/home/GET_SIX_MONTH_SPEND_BY_CURRENCY_SUCCESS'
export const GET_SIX_MONTH_SPEND_BY_CURRENCY_FAILURE =
  'app/home/GET_SIX_MONTH_SPEND_BY_CURRENCY_FAILURE'

export type GetSixMonthSpendByCurrencySuccessPayload = {
  organizationId: string
  lastUpdatedAt: string
  monthSpendDataUSD: MonthlySpendDataResponse[] | null
  monthSpendDataCAD: MonthlySpendDataResponse[] | null
}

export type GetSixMonthSpendByCurrencyErrorPayload = BackendErrorPayload<string>

export interface GetSixMonthSpendByCurrencyInitAction {
  type: typeof GET_SIX_MONTH_SPEND_BY_CURRENCY_INIT
}

export interface GetSixMonthSpendByCurrencySuccessAction {
  type: typeof GET_SIX_MONTH_SPEND_BY_CURRENCY_SUCCESS
  payload: GetSixMonthSpendByCurrencySuccessPayload
}

export interface GetSixMonthSpendByCurrencyFailureAction {
  type: typeof GET_SIX_MONTH_SPEND_BY_CURRENCY_FAILURE
  payload: GetSixMonthSpendByCurrencyErrorPayload
}

export type GetSixMonthSpendByCurrencyActions =
  | GetSixMonthSpendByCurrencyInitAction
  | GetSixMonthSpendByCurrencySuccessAction
  | GetSixMonthSpendByCurrencyFailureAction

const createGetSixMonthSpendByCurrencyInitAction =
  (): GetSixMonthSpendByCurrencyInitAction => ({
    type: GET_SIX_MONTH_SPEND_BY_CURRENCY_INIT,
  })

const createGetSixMonthSpendByCurrencySuccessAction = (
  payload: GetSixMonthSpendByCurrencySuccessPayload,
): GetSixMonthSpendByCurrencySuccessAction => ({
  type: GET_SIX_MONTH_SPEND_BY_CURRENCY_SUCCESS,
  payload,
})

const createGetSixMonthSpendByCurrencyFailureAction = (
  payload: GetSixMonthSpendByCurrencyErrorPayload,
): GetSixMonthSpendByCurrencyFailureAction => ({
  type: GET_SIX_MONTH_SPEND_BY_CURRENCY_FAILURE,
  payload,
})

export const getSixMonthSpendByCurrency = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetSixMonthSpendByCurrencyActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetSixMonthSpendByCurrencyActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetSixMonthSpendByCurrencyInitAction())
    return axios
      .get<GetSixMonthSpendByCurrencySuccessPayload>(
        `/charts/sixmonthspendbycurrency`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetSixMonthSpendByCurrencySuccessAction(response.data))
        },
        (err) => {
          dispatch(
            createGetSixMonthSpendByCurrencyFailureAction(err.response.data),
          )
        },
      )
  }
}

export const GET_ORG_STATS_INIT = 'app/home/GET_ORG_STATS_INIT'
export const GET_ORG_STATS_SUCCESS = 'app/home/GET_ORG_STATS_SUCCESS'
export const GET_ORG_STATS_FAILURE = 'app/home/GET_ORG_STATS_FAILURE'

export type GetOrgStatsSuccessPayload = {
  total: number
  owner: number
  admin: number
  cardManager?: number // TODO: make not optional when all orgs migrated
  bookkeeper: number
  member: number
  guest?: number // TODO: make not optional when all orgs migrated
}

export type GetOrgStatsErrorPayload = BackendErrorPayload<string>

export interface GetOrgStatsInitAction {
  type: typeof GET_ORG_STATS_INIT
}

export interface GetOrgStatsSuccessAction {
  type: typeof GET_ORG_STATS_SUCCESS
  payload: GetOrgStatsSuccessPayload
}

export interface GetOrgStatsFailureAction {
  type: typeof GET_ORG_STATS_FAILURE
  payload: GetOrgStatsErrorPayload
}

export type GetOrgStatsActions =
  | GetOrgStatsInitAction
  | GetOrgStatsSuccessAction
  | GetOrgStatsFailureAction

const createGetOrgStatsInitAction = (): GetOrgStatsInitAction => ({
  type: GET_ORG_STATS_INIT,
})

const createGetOrgStatsSuccessAction = (
  payload: GetOrgStatsSuccessPayload,
): GetOrgStatsSuccessAction => ({
  type: GET_ORG_STATS_SUCCESS,
  payload,
})

const createGetOrgStatsFailureAction = (
  payload: GetOrgStatsErrorPayload,
): GetOrgStatsFailureAction => ({
  type: GET_ORG_STATS_FAILURE,
  payload,
})

export const getOrgStats = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetOrgStatsActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetOrgStatsActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetOrgStatsInitAction())
    return axios
      .get<GetOrgStatsSuccessPayload>(
        `/peoplestatistics`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetOrgStatsSuccessAction(response.data))
        },
        (err) => {
          dispatch(createGetOrgStatsFailureAction(err.response.data))
        },
      )
  }
}
